import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Database, FileVideo, Image as ImageIcon, BookOpen, Pill, Wrench,
  CheckCircle2, AlertTriangle, Loader2, Download, RefreshCw, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useOfflineData } from "@/contexts/OfflineDataContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIDEO_CACHE = "yems-videos-v7";

function fmtTime(ts) {
  if (!ts) return "Never";
  const d = new Date(ts);
  const now = Date.now();
  const diff = Math.floor((now - ts) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

/**
 * Offline Readiness Dashboard — gives paramedics confidence that they are
 * fully cached before heading into offline dead-zones. Aggregates protocol,
 * drug, procedure, video, and image cache state and offers a single
 * "Sync everything" action.
 */
export default function OfflineReadinessDialog({ open, onOpenChange, trigger }) {
  const {
    protocols, drugs, procedures, symptoms, categories,
    lastSync, syncing, syncAllData, isOnline, dataReady,
  } = useOfflineData();

  const [videos, setVideos] = useState([]);
  const [videoLoading, setVideoLoading] = useState(false);
  const [imageCacheCount, setImageCacheCount] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, label: "" });

  const refreshVideos = useCallback(async () => {
    setVideoLoading(true);
    try {
      const res = await axios.get(`${API}/videos`);
      const list = res.data?.videos || [];
      let cache = null;
      if ("caches" in window) {
        try { cache = await caches.open(VIDEO_CACHE); } catch { cache = null; }
      }
      const enriched = await Promise.all(list.map(async (v) => {
        let cached = false;
        if (cache) {
          const hit = await cache.match(v.path);
          cached = !!hit;
        }
        return { ...v, cached };
      }));
      setVideos(enriched);
    } catch {
      // Network failed — leave existing list
    } finally {
      setVideoLoading(false);
    }
  }, []);

  const refreshImages = useCallback(async () => {
    if (!("caches" in window)) return;
    try {
      const names = await caches.keys();
      const imageCaches = names.filter((n) => n.includes("images") || n.includes("yems-img"));
      let count = 0;
      for (const n of imageCaches) {
        const c = await caches.open(n);
        const keys = await c.keys();
        count += keys.length;
      }
      setImageCacheCount(count);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (open) {
      refreshVideos();
      refreshImages();
    }
  }, [open, refreshVideos, refreshImages]);

  // Aggregate readiness
  const protocolsReady = protocols.length > 0;
  const drugsReady = drugs.length > 0;
  const proceduresReady = procedures.length > 0;
  const totalVideos = videos.length;
  const cachedVideos = videos.filter((v) => v.cached).length;
  const totalMB = videos.reduce((s, v) => s + (v.size_mb || 0), 0);
  const cachedMB = videos.filter((v) => v.cached).reduce((s, v) => s + (v.size_mb || 0), 0);
  const allVideosCached = totalVideos > 0 && cachedVideos === totalVideos;

  const sections = [
    {
      key: "protocols",
      icon: BookOpen,
      label: "Clinical Protocols",
      count: protocols.length,
      total: protocols.length,
      ready: protocolsReady,
      detail: `${categories.length} categor${categories.length === 1 ? "y" : "ies"} · ${symptoms.length} symptom${symptoms.length === 1 ? "" : "s"}`,
    },
    {
      key: "drugs",
      icon: Pill,
      label: "Drug Reference",
      count: drugs.length,
      total: drugs.length,
      ready: drugsReady,
      detail: drugs.length ? `${drugs.length} drugs cached locally` : "Not yet cached",
    },
    {
      key: "procedures",
      icon: Wrench,
      label: "Procedures",
      count: procedures.length,
      total: procedures.length,
      ready: proceduresReady,
      detail: procedures.length ? `${procedures.length} procedures cached locally` : "Not yet cached",
    },
    {
      key: "videos",
      icon: FileVideo,
      label: "Embedded Videos",
      count: cachedVideos,
      total: totalVideos,
      ready: allVideosCached,
      detail: `${cachedMB.toFixed(1)} / ${totalMB.toFixed(1)} MB`,
      partial: totalVideos > 0 && cachedVideos > 0 && !allVideosCached,
    },
    {
      key: "images",
      icon: ImageIcon,
      label: "Images & Diagrams",
      count: imageCacheCount,
      total: imageCacheCount,
      ready: imageCacheCount > 0,
      detail: imageCacheCount > 0 ? `${imageCacheCount} files cached by service worker` : "Visit pages once online to cache",
    },
  ];

  const allReady = sections.every((s) => s.ready);
  const readyCount = sections.filter((s) => s.ready).length;

  // Master "Sync everything" action: refresh data, then download videos
  const syncEverything = async () => {
    if (!isOnline) {
      toast.error("You're offline. Reconnect to sync.");
      return;
    }
    setDownloading(true);
    try {
      // Step 1: protocols + drugs + procedures
      setProgress({ done: 0, total: 100, label: "Syncing protocols, drugs & procedures…" });
      await syncAllData(true);
      setProgress({ done: 50, total: 100, label: "Caching images…" });
      // Image precache is fired by syncAllData via SW message
      await new Promise((r) => setTimeout(r, 600));

      // Step 2: videos
      if ("caches" in window) {
        const cache = await caches.open(VIDEO_CACHE);
        const list = videos.length ? videos : (await axios.get(`${API}/videos`)).data?.videos || [];
        const todo = [];
        for (const v of list) {
          const hit = await cache.match(v.path);
          if (!hit) todo.push(v);
        }
        let done = 0;
        for (const v of todo) {
          setProgress({ done: 50 + Math.floor((done / Math.max(todo.length, 1)) * 50), total: 100, label: `Downloading ${v.filename}…` });
          try {
            const r = await fetch(v.path);
            if (r.ok) await cache.put(v.path, r.clone());
          } catch {
            // ignore individual failures
          }
          done++;
        }
      }

      setProgress({ done: 100, total: 100, label: "Finalising…" });
      await refreshVideos();
      await refreshImages();
      toast.success("Device is ready for offline use", {
        description: `${protocols.length} protocols · ${drugs.length} drugs · ${cachedVideos}/${totalVideos} videos`,
      });
    } catch (e) {
      toast.error("Sync incomplete — check connection and retry");
    } finally {
      setDownloading(false);
      setProgress({ done: 0, total: 0, label: "" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger}
      <DialogContent className="max-w-md max-h-[90vh] overflow-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <ShieldCheck className={`w-5 h-5 ${allReady ? "text-emerald-400" : "text-amber-400"}`} />
            Offline Readiness
          </DialogTitle>
          <DialogDescription className="sr-only">
            Verify that protocols, drugs, videos and images are saved on this device for offline use.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Hero status */}
          <div
            className={`rounded-lg border-2 p-4 ${allReady ? "border-emerald-500/40 bg-emerald-500/10" : "border-amber-500/40 bg-amber-500/10"}`}
            data-testid="readiness-hero"
          >
            <div className="flex items-center gap-3">
              {allReady ? (
                <CheckCircle2 className="w-8 h-8 text-emerald-400 shrink-0" />
              ) : (
                <AlertTriangle className="w-8 h-8 text-amber-400 shrink-0" />
              )}
              <div className="flex-1">
                <div className={`font-heading font-bold ${allReady ? "text-emerald-300" : "text-amber-300"}`} data-testid="readiness-status">
                  {allReady ? "Ready for offline use" : `${readyCount} of ${sections.length} components ready`}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  Last sync: {fmtTime(lastSync)} · {isOnline ? <span className="text-emerald-400">Online</span> : <span className="text-amber-400">Offline</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-2" data-testid="readiness-sections">
            {sections.map((s) => {
              const Icon = s.icon;
              const isPartial = s.partial;
              return (
                <div
                  key={s.key}
                  className={`flex items-center gap-3 rounded-lg border p-3 ${s.ready ? "border-emerald-500/30 bg-emerald-500/5" : isPartial ? "border-amber-500/30 bg-amber-500/5" : "border-border bg-secondary/10"}`}
                  data-testid={`readiness-${s.key}`}
                >
                  <Icon className={`w-5 h-5 shrink-0 ${s.ready ? "text-emerald-400" : isPartial ? "text-amber-400" : "text-muted-foreground"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-2">
                      {s.label}
                      {s.ready ? (
                        <Badge className="bg-emerald-600 text-white text-[10px] px-1.5">Saved</Badge>
                      ) : isPartial ? (
                        <Badge className="bg-amber-600 text-white text-[10px] px-1.5">Partial</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] px-1.5">Missing</Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">{s.detail}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-mono text-sm font-bold ${s.ready ? "text-emerald-400" : isPartial ? "text-amber-400" : "text-muted-foreground"}`}>
                      {s.total ? `${s.count}/${s.total}` : s.count}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Progress */}
          {downloading && (
            <div className="space-y-1.5" data-testid="readiness-progress">
              <div className="flex justify-between text-[11px] text-muted-foreground">
                <span className="truncate">{progress.label}</span>
                <span className="tabular-nums shrink-0 ml-2">{progress.done}%</span>
              </div>
              <Progress value={progress.done} className="h-2" />
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              onClick={syncEverything}
              disabled={!isOnline || syncing || downloading || !dataReady}
              className="flex-1 bg-cyan-600 hover:bg-cyan-700"
              data-testid="readiness-sync-all"
            >
              {downloading ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Syncing…</>
              ) : allReady ? (
                <><RefreshCw className="w-4 h-4 mr-1.5" />Re-sync everything</>
              ) : (
                <><Download className="w-4 h-4 mr-1.5" />Sync everything</>
              )}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => { refreshVideos(); refreshImages(); }}
              disabled={downloading || videoLoading}
              title="Refresh status"
              data-testid="readiness-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${videoLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>

          <p className="text-[11px] text-muted-foreground italic flex items-start gap-1.5">
            <Database className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              Tap <strong>Sync everything</strong> while online to cache all protocols, drugs, videos and images on this device. Cached content stays available without a connection.
            </span>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
